import { useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  FileText,
  Info,
  X,
} from "lucide-react";
import {
  validateDocumentsForControl,
  listGovernanceDocuments,
  type ValidatedSupportingDocument,
  type ValidateDocumentsResult,
  type GovernanceDocumentSummaryResponse,
} from "../../api/frameworksApi";
import { useAuthStore } from "../../stores/authStore";

/* ── Types ─────────────────────────────────────────── */

interface DocOption {
  id: string;
  name: string;
  /** Present only when data comes from the validate API */
  analysis?: ValidatedSupportingDocument;
}

/* ── Helpers ────────────────────────────────────────── */

function statusLabel(status: string): string {
  switch (status) {
    case "SATISFIED":
      return "Full match";
    case "PARTIALLY_SATISFIED":
      return "Partial match";
    case "NOT_SATISFIED":
      return "No match";
    default:
      return status;
  }
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case "SATISFIED":
      return "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400";
    case "PARTIALLY_SATISFIED":
      return "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400";
    case "NOT_SATISFIED":
      return "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400";
    default:
      return "bg-muted text-muted-foreground";
  }
}

function confidenceBadgeClass(level: string): string {
  switch (level) {
    case "HIGH":
      return "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400";
    case "MEDIUM":
      return "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400";
    case "LOW":
      return "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400";
    default:
      return "bg-muted text-muted-foreground";
  }
}

/* ── Side Drawer ─────────────────────────────────────── */

function FullDocumentAnalysisDrawer({
  doc,
  recommendation,
  onClose,
}: {
  doc: ValidatedSupportingDocument;
  recommendation?: { priority: string; summary: string; actions: string[] };
  onClose: () => void;
}) {
  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/20 dark:bg-black/40"
        onClick={onClose}
      />
      {/* Drawer panel */}
      <div className="fixed right-0 top-0 z-50 h-full w-[420px] max-w-full bg-white dark:bg-[#131320] shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <FileText className="size-4 text-muted-foreground flex-shrink-0" />
            <span className="text-sm font-semibold text-foreground">
              Document match analysis
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md hover:bg-muted transition-colors flex-shrink-0"
          >
            <X className="size-4 text-muted-foreground" />
          </button>
        </div>

        {/* Doc name subheader */}
        <div className="px-5 pt-3 pb-1 flex-shrink-0">
          <p className="text-[13px] font-semibold text-foreground truncate">
            {doc.documentName}
          </p>
        </div>

        {/* Status badges */}
        <div className="flex items-center gap-2 px-5 py-2 border-b border-border flex-shrink-0 flex-wrap">
          <span
            className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${statusBadgeClass(doc.validationStatus)}`}
          >
            {statusLabel(doc.validationStatus)}
          </span>
          <span
            className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${confidenceBadgeClass(doc.confidence.level)}`}
          >
            {doc.confidence.score}% confidence - {doc.confidence.level}
          </span>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {/* Why suggested */}
          {recommendation?.summary && (
            <section>
              <p className="text-[10px] font-bold tracking-wider text-amber-600 dark:text-amber-400 mb-2">
                Why this document was suggested
              </p>
              <div className="text-[12px] text-foreground/80 leading-relaxed bg-muted/30 rounded-lg p-3 border border-border">
                {recommendation.summary}
              </div>
            </section>
          )}

          {/* What document covers */}
          {doc.found.length > 0 && (
            <section>
              <p className="text-[10px] font-bold tracking-wider text-emerald-600 dark:text-emerald-400 mb-2">
                What this document covers
              </p>
              <ul className="space-y-1.5">
                {doc.found.map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle2 className="size-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-[12px] text-foreground/80 leading-relaxed">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* What it doesn't cover */}
          {recommendation?.actions && recommendation.actions.length > 0 && (
            <section>
              <p className="text-[10px] font-bold tracking-wider text-amber-600 dark:text-amber-400 mb-2">
                What this document does not cover
              </p>
              <ul className="space-y-1.5">
                {recommendation.actions.map((action, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <AlertTriangle className="size-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <span className="text-[12px] text-foreground/80 leading-relaxed">
                      {action}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Evidence found */}
          {doc.chunks.length > 0 && (
            <section>
              <p className="text-[10px] font-bold tracking-wider text-muted-foreground mb-2">
                Evidence found in this document
              </p>
              <div className="space-y-2">
                {doc.chunks.map((chunk) => (
                  <div
                    key={chunk.chunkId}
                    className="rounded-lg border border-border bg-muted/20 px-3 py-2"
                  >
                    <p className="text-[12px] font-semibold text-foreground">
                      {chunk.sectionTitle}
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      {chunk.pageRange}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Gap summary */}
          {recommendation?.summary && (
            <section>
              <p className="text-[10px] font-bold tracking-wider text-muted-foreground mb-2">
                Gap summary
              </p>
              <div className="text-[12px] text-foreground/80 leading-relaxed bg-muted/30 rounded-lg p-3 border border-border">
                {recommendation.summary}
              </div>
            </section>
          )}
        </div>
      </div>
    </>
  );
}

/* ── Main component ──────────────────────────────────── */

export function ValidationDocPicker({
  controlId,
  controlTitle = "",
  controlStatement = "",
  value,
  onChange,
  orgId: orgIdProp,
}: {
  controlId: string;
  controlTitle?: string;
  controlStatement?: string;
  value: string;
  onChange: (id: string, name: string) => void;
  /** Explicit org id (e.g. the SSP's owningOrgUnitId). Falls back to the
   *  logged-in user's orgId when not provided. */
  orgId?: string | null;
}) {
  const storeOrgId = useAuthStore((s) => s.user?.orgId) ?? "";
  // Fallback: read orgId directly from sessionStorage in case the auth store's
  // in-memory user isn't populated yet (avoids sending an empty orgId to the
  // validate API, which rejects it).
  const orgId = (() => {
    if (storeOrgId) return storeOrgId;
    try {
      const raw = sessionStorage.getItem("auth_user");
      if (!raw) return "";
      const parsed = JSON.parse(raw);
      return parsed?.orgId ?? "";
    } catch {
      return "";
    }
  })();
  const [options, setOptions] = useState<DocOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [validateResult, setValidateResult] = useState<ValidateDocumentsResult | null>(null);
  const [drawerDoc, setDrawerDoc] = useState<ValidatedSupportingDocument | null>(null);

  const rootRef = useRef<HTMLDivElement>(null);

  /* Fetch documents — governance list first (for IDs + org), then validate */
  useEffect(() => {
    if (!controlId) return;
    let cancelled = false;
    setLoading(true);

    const run = async () => {
      // Step 1: load governance documents. Their IDs feed the validate API's
      // documentIds field, and their owningOrgUnitId is used as a fallback
      // orgId when the auth store's user record doesn't carry one.
      let govDocs: GovernanceDocumentSummaryResponse[] = [];
      try {
        const govResp = await listGovernanceDocuments();
        govDocs = govResp.data ?? [];
      } catch {
        if (!cancelled) {
          setOptions([]);
          setLoading(false);
        }
        return;
      }

      if (govDocs.length === 0) {
        if (!cancelled) {
          setOptions([]);
          setLoading(false);
        }
        return;
      }

      // Resolve orgId: auth store → first document's owning org unit.
      const effectiveOrgId =
        orgId || govDocs.find((d) => d.owningOrgUnitId)?.owningOrgUnitId || "";

      // Filter docs to the resolved org (if we have one) so we don't send
      // documentIds that belong to a different org than what we're validating
      // for. Without a resolved org, send everything and let the backend scope.
      const scopedDocs = effectiveOrgId
        ? govDocs.filter(
            (d) => !d.owningOrgUnitId || d.owningOrgUnitId === effectiveOrgId,
          )
        : govDocs;
      const documentIds = scopedDocs.map((d) => d.id);

      // Step 2: try the validate API to get per-document analysis.
      try {
        const res = await validateDocumentsForControl({
          orgId: effectiveOrgId,
          documentIds,
          control: {
            controlId,
            controlTitle,
            controlStatement,
          },
        });
        const result = res?.data;
        const supporting = result?.supportingDocuments ?? [];
        if (!cancelled && supporting.length > 0) {
          setValidateResult(result ?? null);
          setOptions(
            supporting.map((d) => ({
              id: d.documentId,
              name: d.documentName,
              analysis: d,
            })),
          );
          setLoading(false);
          return;
        }
      } catch {
        // fall through to raw governance list
      }

      // Fallback: show the governance docs without per-doc validation analysis.
      if (!cancelled) {
        setOptions(scopedDocs.map((d) => ({ id: d.id, name: d.title })));
        setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [controlId, controlTitle, controlStatement, orgId]);

  /* Close dropdown on outside click */
  useEffect(() => {
    if (!open) return;
    function handleOutside(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open]);

  const selectedOption = options.find((o) => o.id === value);

  return (
    <div className="relative" ref={rootRef}>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] bg-[#f5f7fb] dark:bg-white/[0.04] text-[#0f172a] dark:text-white px-3 h-[38px] text-sm hover:border-indigo-400/60 dark:hover:border-indigo-400/40 transition-colors outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-indigo-500/10"
      >
        <span className="truncate text-left">
          {loading ? (
            <span className="text-muted-foreground">Loading documents...</span>
          ) : selectedOption ? (
            selectedOption.name
          ) : (
            <span className="text-muted-foreground">
              Select a validation document...
            </span>
          )}
        </span>
        <ChevronDown
          className={`size-4 text-muted-foreground flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {/* Dropdown */}
      {open && !loading && (
        <div className="absolute left-0 right-0 top-full mt-1 z-30 bg-white dark:bg-[#1a1a2e] border border-border rounded-lg shadow-lg max-h-56 overflow-y-auto">
          {options.length === 0 ? (
            <div className="px-3 py-2.5 text-xs text-muted-foreground">
              No documents available
            </div>
          ) : (
            options.map((opt) => (
              <div
                key={opt.id}
                className={`flex items-center gap-2 px-3 py-2 cursor-pointer group hover:bg-muted/50 transition-colors ${value === opt.id ? "bg-indigo-50 dark:bg-indigo-500/10" : ""}`}
              >
                {/* Selectable area */}
                <button
                  type="button"
                  className="flex-1 text-left text-[13px] text-foreground min-w-0"
                  onClick={() => {
                    onChange(opt.id, opt.name);
                    setOpen(false);
                  }}
                >
                  <span className="truncate block">{opt.name}</span>
                  {opt.analysis && (
                    <span
                      className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full mt-0.5 inline-block ${statusBadgeClass(opt.analysis.validationStatus)}`}
                    >
                      {statusLabel(opt.analysis.validationStatus)}
                    </span>
                  )}
                </button>

                {/* Info icon — only when analysis is available */}
                {opt.analysis && (
                  <button
                    type="button"
                    title="View document analysis"
                    className="p-1 rounded-md text-muted-foreground hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-colors flex-shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpen(false);
                      setDrawerDoc(opt.analysis!);
                    }}
                  >
                    <Info className="size-3.5" />
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Selected document preview */}
      {selectedOption && (
        <div className="flex items-center gap-2.5 rounded-lg border border-border bg-muted/30 px-3 py-2.5 mt-2">
          <FileText className="size-3.5 text-muted-foreground flex-shrink-0" />
          <span className="text-xs text-foreground flex-1 truncate">
            {selectedOption.name}
          </span>
          {selectedOption.analysis && (
            <>
              <button
                type="button"
                title="View document analysis"
                className="text-muted-foreground hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                onClick={() => setDrawerDoc(selectedOption.analysis!)}
              >
                <Info className="size-3.5" />
              </button>
              <span
                className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${statusBadgeClass(selectedOption.analysis.validationStatus)}`}
              >
                {statusLabel(selectedOption.analysis.validationStatus)}
              </span>
            </>
          )}
        </div>
      )}

      {/* Side drawer */}
      {drawerDoc && (
        <FullDocumentAnalysisDrawer
          doc={drawerDoc}
          recommendation={validateResult?.recommendation}
          onClose={() => setDrawerDoc(null)}
        />
      )}
    </div>
  );
}
